import { useMemo } from "react";

import { cn, platformProps } from "../../lib";
import { webOnly, webRole } from "../../lib/web-role";
import { Box } from "../../primitives";
import { ChatBubbleContext } from "./chat-bubble-context";
import type { ChatBubbleContextValue, ChatBubbleProps } from "./types";

/** Row alignment + spacing. Avatar position flips by variant. */
const rowVariantClass = {
  sender: "flex-row-reverse",
  receiver: "flex-row",
  // A row, so the bubble centres across it on native too (a native Box is a
  // column by default)
  system: "flex-row justify-center",
};

export const ChatBubbleRoot = ({
  variant = "receiver",
  className,
  testID,
  accessibilityLabel,
  children,
}: ChatBubbleProps) => {
  const contextValue = useMemo<ChatBubbleContextValue>(() => ({ variant }), [variant]);
  const label = accessibilityLabel || undefined;

  return (
    <ChatBubbleContext.Provider value={contextValue}>
      <Box
        // On web a label needs a group (ARIA gives a role-less div no name).
        // Native keeps its static-text trait and no label: its texts name it,
        // and reading the label would need the bubble to be one element,
        // which hides the attachment buttons inside it from screen readers
        accessibilityRole={label ? webRole("group", "text") : "text"}
        accessibilityLabel={webOnly(label)}
        className={cn(
          "gap-spacing-sm my-spacing-xs flex w-full items-end",
          rowVariantClass[variant],
          className,
        )}
        {...platformProps({ testID, slot: "chat-bubble" })}
      >
        {children}
      </Box>
    </ChatBubbleContext.Provider>
  );
};

ChatBubbleRoot.displayName = "ChatBubble";
